import React, { useEffect, useState, useRef } from 'react';
import { ChevronLeft, ChevronRight, ChevronUp, ChevronDown, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { useApplicationSettingsContext } from '../../contexts/ApplicationSettingsContext';
import { usePermissionCheck } from '../../hooks/usePermissionCheck';
import { format } from 'date-fns';

const statuses = [
  { value: 'created', label: 'Created' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' }
];

interface MobileTabStatusHeaderProps {
  // Tab props
  activeTab: string;
  onTabChange: (tab: string) => void;
  
  // Status props - now optional since status will be separate
  status?: string;
  createdAt?: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
}

interface MobileStatusControllerProps {
  status: string;
  createdAt: string;
  completedAt?: string | null;
  onStatusChange?: (status: string) => void;
  readOnly?: boolean;
}

export function MobileStatusController({ 
  status, 
  createdAt, 
  completedAt, 
  onStatusChange, 
  readOnly = false 
}: MobileStatusControllerProps) {
  // Status navigation logic
  const currentStatusIndex = statuses.findIndex(s => s.value === status);
  const canStatusGoUp = currentStatusIndex > 0;
  const canStatusGoDown = currentStatusIndex < statuses.length - 1;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'created':
        return 'text-gray-400';
      case 'scheduled':
        return 'text-blue-400';
      case 'in_progress':
        return 'text-yellow-400';
      case 'completed':
        return 'text-green-400';
      default:
        return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 className="h-3 w-3" />;
      case 'cancelled':
        return <XCircle className="h-3 w-3 text-red-400" />;
      default:
        return <Clock className="h-3 w-3" />;
    }
  };

  const handleStatusUp = () => {
    if (canStatusGoUp && onStatusChange && !readOnly) {
      onStatusChange(statuses[currentStatusIndex - 1].value);
    }
  };

  const handleStatusDown = () => {
    if (canStatusGoDown && onStatusChange && !readOnly) {
      onStatusChange(statuses[currentStatusIndex + 1].value);
    }
  };

  // Special handling for cancelled status
  if (status === 'cancelled') {
    return (
      <div className="bg-[#1a1a1a] border-b border-[#2f2f2f] px-3 py-2">
        <div className="flex justify-center">
          <div className="flex items-center space-x-1">
            <XCircle className="h-3 w-3 text-red-400" />
            <span className="text-red-400 text-xs font-medium">Cancelled</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#1a1a1a] border-b border-[#2f2f2f] px-3 py-2">
      <div className="flex justify-center">
        {/* Status Controller - Centered */}
        <div className="flex items-center space-x-1">
          {/* Status Up Arrow */}
          <button
            onClick={handleStatusUp}
            disabled={readOnly || !canStatusGoUp}
            className={`p-0.5 rounded transition-colors ${
              canStatusGoUp && !readOnly
                ? 'text-gray-400 hover:text-white' 
                : 'text-gray-600 cursor-not-allowed'
            }`}
          >
            <ChevronUp className="h-3 w-3" />
          </button>

          {/* Current Status */}
          <div className="flex flex-col items-center space-y-0.5">
            <div className={`flex items-center space-x-1 ${getStatusColor(status)}`}>
              {getStatusIcon(status)}
              <span className="text-xs font-medium whitespace-nowrap">
                {statuses[currentStatusIndex]?.label}
              </span>
            </div>
            
            {/* Status Progress Dots */}
            <div className="flex space-x-0.5">
              {statuses.map((_, index) => (
                <div
                  key={index}
                  className={`h-0.5 w-2 rounded-full transition-colors ${
                    index <= currentStatusIndex ? 'bg-white' : 'bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Status Down Arrow */}
          <button
            onClick={handleStatusDown}
            disabled={readOnly || !canStatusGoDown}
            className={`p-0.5 rounded transition-colors ${
              canStatusGoDown && !readOnly
                ? 'text-gray-400 hover:text-white' 
                : 'text-gray-600 cursor-not-allowed'
            }`}
          >
            <ChevronDown className="h-3 w-3" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MobileTabStatusHeader({ 
  activeTab, 
  onTabChange
}: MobileTabStatusHeaderProps) {
  const { disabledPages } = useApplicationSettingsContext();
  const { canAccess } = usePermissionCheck();
  
  // Simplified drag state
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragCurrentX, setDragCurrentX] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Check if pages are disabled in application settings or by permissions
  const isPnLDisabled = !canAccess.bookingProfitLoss;
  const isMaterialsDisabled = disabledPages.includes('materials') || !canAccess.bookingMaterials;
  const isFlatRateDisabled = disabledPages.includes('flat-rates') || !canAccess.bookingRatebook;

  const tabs = [
    { id: 'overview', label: 'Overview' },
    // Only show materials tab if enabled in application settings and user has permission
    ...(isMaterialsDisabled ? [] : [{ id: 'materials', label: 'Materials' }]),
    // Only show flat rates tab if enabled in application settings and user has permission
    ...(isFlatRateDisabled ? [] : [{ id: 'flat-rates', label: 'RateBook' }]),
    // Only show profit & loss tab if user has permission
    ...(isPnLDisabled ? [] : [{ id: 'profit-loss', label: 'Profit & Loss' }])
  ];

  // Tab navigation logic
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);

  // If the active tab is not in the available tabs, switch to overview
  useEffect(() => {
    if (activeTab !== 'overview' && !tabs.some(tab => tab.id === activeTab)) {
      onTabChange('overview');
    }
  }, [activeTab, tabs, onTabChange]);

  // Circular navigation helpers
  const getPreviousTabIndex = () => {
    return currentTabIndex === 0 ? tabs.length - 1 : currentTabIndex - 1;
  };

  const getNextTabIndex = () => {
    return currentTabIndex === tabs.length - 1 ? 0 : currentTabIndex + 1;
  };

  const handlePreviousTabCircular = () => {
    const prevIndex = getPreviousTabIndex();
    const prevTab = tabs[prevIndex];
    if (prevTab) {
      onTabChange(prevTab.id);
    }
  };

  const handleNextTabCircular = () => {
    const nextIndex = getNextTabIndex();
    const nextTab = tabs[nextIndex];
    if (nextTab) {
      onTabChange(nextTab.id);
    }
  };

  // Simplified drag handlers
  const handleDragStart = (clientX: number) => {
    setIsDragging(true);
    setDragStartX(clientX);
    setDragCurrentX(clientX);
  };

  const handleDragMove = (clientX: number) => {
    if (!isDragging) return;
    setDragCurrentX(clientX);
  };

  const handleDragEnd = () => {
    if (!isDragging) return;
    
    const dragDistance = dragCurrentX - dragStartX;
    const threshold = 30; // Reduced threshold for easier navigation
    
    if (Math.abs(dragDistance) > threshold) {
      if (dragDistance > 0) {
        // Dragged right - go to previous tab
        handlePreviousTabCircular();
      } else {
        // Dragged left - go to next tab
        handleNextTabCircular();
      }
    }
    
    // Reset drag state
    setIsDragging(false);
    setDragStartX(0);
    setDragCurrentX(0);
  };

  // Mouse events
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    handleDragStart(e.clientX);
  };

  // Touch events
  const handleTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    handleDragStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    e.preventDefault();
    handleDragMove(e.touches[0].clientX);
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    e.preventDefault();
    handleDragEnd();
  };

  // Add global mouse listeners when dragging
  useEffect(() => {
    if (!isDragging) return;

    const handleGlobalMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientX);
    };

    const handleGlobalMouseUp = () => {
      handleDragEnd();
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDragging, dragStartX, dragCurrentX]);

  // Calculate drag offset for visual feedback
  const getDragOffset = () => {
    if (!isDragging) return 0;
    const offset = dragCurrentX - dragStartX;
    // Limit the visual drag to prevent excessive movement
    return Math.max(-60, Math.min(60, offset));
  };

  return (
    <div className="bg-[#1a1a1a] border-b border-[#2f2f2f] px-3 py-2">
      {/* Tab Carousel - Centered Layout */}
      <div className="flex-1 relative" ref={containerRef}>
        {/* Previous Tab (left, faded) */}
        <div className="text-xs text-gray-500 absolute left-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {tabs[getPreviousTabIndex()]?.label}
        </div>

        {/* Fixed Navigation Layout */}
        <div className="flex items-center justify-center relative">
          {/* Left Arrow - Close to Center */}
          <button
            onClick={handlePreviousTabCircular}
            className="absolute left-20 p-1 rounded-full transition-colors text-white hover:bg-gray-700 focus:outline-none z-10"
            style={{ 
              WebkitTapHighlightColor: 'transparent', 
              outline: 'none',
              WebkitAppearance: 'none',
              appearance: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              background: 'transparent',
              border: 'none'
            }}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {/* Current Tab - Balanced touch target and spacing */}
          <div 
            className={`text-center px-3 py-2 select-none transition-transform duration-150 ${isDragging ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{ 
              transform: `translateX(${getDragOffset()}px)`
            }}
            onMouseDown={handleMouseDown}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <h3 className="text-white font-medium text-lg whitespace-nowrap pointer-events-none">
              {tabs[currentTabIndex]?.label}
            </h3>
          </div>

          {/* Right Arrow - Close to Center */}
          <button
            onClick={handleNextTabCircular}
            className="absolute right-20 p-1 rounded-full transition-colors text-white hover:bg-gray-700 focus:outline-none z-10"
            style={{ 
              WebkitTapHighlightColor: 'transparent', 
              outline: 'none',
              WebkitAppearance: 'none',
              appearance: 'none',
              userSelect: 'none',
              WebkitUserSelect: 'none',
              background: 'transparent',
              border: 'none'
            }}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        {/* Next Tab (right, faded) */}
        <div className="text-xs text-gray-500 absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
          {tabs[getNextTabIndex()]?.label}
        </div>

        {/* Tab Progress Dots - Fixed Position */}
        <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-0.5">
          <div className="flex space-x-1 pointer-events-none">
            {tabs.map((_, index) => (
              <div
                key={index}
                className={`h-1 w-3 rounded-full transition-colors ${
                  index === currentTabIndex ? 'bg-white' : 'bg-gray-600'
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
} 